import { v4 as uuidv4 } from 'uuid'
import { editingLineItemType, lineItemType, shipToType } from '../types'

export function lineKey(item: { Kit?: string | null; Number?: number | null }): string {
  return `${item.Kit ?? ''}-${item.Number ?? -2}`
}

export function renumberLines<T extends { Number: number | null }>(lines: T[]): T[] {
  let nextNum = 1
  return lines.map(line => ({
    ...line,
    Number: line.Number === -1 ? -1 : nextNum++,
  }))
}

export function findLineIndex(lines: editingLineItemType[], lineItem: editingLineItemType): number {
  return lines.findIndex(item => lineKey(item) === lineKey(lineItem))
}

export function createBlankEditingLine(
  kit: string | null,
  number: number,
  productDescription: string | null,
  mpn: string | null,
  quantity: number | null,
  unitPrice: number | null,
  extPrice: number | null,
  displayPartNumber: string | null,
): editingLineItemType {
  return {
    Id: uuidv4(),
    ProductDescription: productDescription || null,
    MfrPartNum: mpn || null,
    Quantity: quantity || null,
    UnitPrice: unitPrice || null,
    ExtPrice: extPrice || null,
    UnitCost: null,
    ExtCost: null,
    SupplierPartNum: null,
    ProductCategory: null,
    Supplier: null,
    Class: null,
    Number: number,
    Kit: kit,
    Manufacturer: null,
    ShipToCompany: null,
    ShipToAttention: null,
    ShipToAddress1: null,
    ShipToAddress2: null,
    ShipToAddressCity: null,
    ShipToAddressZip: null,
    ShipToAddressState: null,
    ShipToAddressCountry: null,
    TaaCompliant: null,
    ContractCeilingPrice: null,
    PartnerFee: null,
    ContractFee: null,
    TotalFee: null,
    CountryOfOrigin: null,
    PopStartDate: null,
    PopEndDate: null,
    ServiceTag: null,
    SerialNumber: null,
    UnitOfMeasure: null,
    Notes: null,
    Bundle: false,
    editing: false,
    addressId: null,
    selected: false,
    ReceiverContactName: null,
    ReceiverContactEmail: null,
    ReceiverContactPhone: null,
    NetsuiteVendor: null,
    DisplayPartNumber: displayPartNumber || null,
  }
}

export function duplicateLineItem(lines: editingLineItemType[], lineItem: editingLineItemType): editingLineItemType[] {
  const idx = findLineIndex(lines, lineItem)
  if (idx === -1) return lines
  const copy = { ...lineItem, editing: false, addressId: null, selected: false, Id: '' }
  const inserted = [...lines.slice(0, idx + 1), copy, ...lines.slice(idx + 1)]
  return renumberLines(inserted)
}

export function addBlankLineAfter(
  lines: editingLineItemType[],
  lineItem: editingLineItemType,
  newLine: editingLineItemType,
): editingLineItemType[] {
  const idx = findLineIndex(lines, lineItem)
  if (idx === -1) return lines
  return renumberLines([...lines.slice(0, idx + 1), newLine, ...lines.slice(idx + 1)])
}

export function deleteLineItem(lines: editingLineItemType[], lineItem: editingLineItemType): editingLineItemType[] {
  if (lineItem.Number === -1) {
    const kit = lineItem.Kit ?? ''
    return renumberLines(lines.filter(item => item.Kit !== kit))
  }
  return renumberLines(lines.filter(item => lineKey(item) !== lineKey(lineItem)))
}

export function deleteKit(lines: editingLineItemType[], kit: string): editingLineItemType[] {
  return renumberLines(lines.filter(item => item.Kit !== kit))
}

export function deleteSelectedLines(lines: editingLineItemType[]): editingLineItemType[] {
  const selectedNumbers = new Set(lines.filter(item => item.selected).map(item => item.Number))
  return renumberLines(lines.filter(item => !selectedNumbers.has(item.Number ?? -1)))
}

export function mergeBundleIntoLines(lines: editingLineItemType[], mergedLine: lineItemType): editingLineItemType[] {
  const selectedNumbers = new Set(lines.filter(item => item.selected).map(item => item.Number))
  const firstSelectedIndex = lines.findIndex(item => item.selected)
  if (firstSelectedIndex === -1) return lines
  const mergedForEditing: editingLineItemType = {
    ...mergedLine,
    editing: false,
    addressId: null,
    selected: false,
  }
  const withoutSelected = lines.filter(item => !selectedNumbers.has(item.Number ?? -1))
  const beforeCount = lines.slice(0, firstSelectedIndex).filter(item => !selectedNumbers.has(item.Number ?? -1)).length
  return renumberLines([
    ...withoutSelected.slice(0, beforeCount),
    mergedForEditing,
    ...withoutSelected.slice(beforeCount),
  ])
}

export function bulkEdit(
  lines: editingLineItemType[],
  classVal: string,
  productCategoryVal: string,
  vendorVal: string,
  popStartDate: Date | null,
  popEndDate: Date | null,
): editingLineItemType[] {
  return lines.map(item =>
    item.selected
      ? {
          ...item,
          ...(classVal !== '' && { Class: classVal }),
          ...(productCategoryVal !== '' && { ProductCategory: productCategoryVal }),
          ...(vendorVal !== '' && { NetsuiteVendor: vendorVal }),
          ...(popStartDate !== null && { PopStartDate: popStartDate }),
          ...(popEndDate !== null && { PopEndDate: popEndDate }),
        }
      : item,
  )
}

export type BulkShippingAddressSource = shipToType & { id: number }

export function bulkApplyShippingAddress(
  lines: editingLineItemType[],
  address: BulkShippingAddressSource,
): editingLineItemType[] {
  return lines.map(item => {
    if (!item.selected) return item
    const base = {
      ...item,
      ShipToAttention: address.ShipToAttention,
      ShipToCompany: address.ShipToCompany,
      ShipToAddress1: address.ShipToAddress1,
      ShipToAddress2: address.ShipToAddress2,
      ShipToAddressCity: address.ShipToAddressCity,
      ShipToAddressState: address.ShipToAddressState,
      ShipToAddressZip: address.ShipToAddressZip,
      ShipToAddressCountry: address.ShipToAddressCountry,
      addressId: address.id,
    }
    if (address.ReceiverContactName) {
      return {
        ...base,
        ReceiverContactName: address.ReceiverContactName,
        ReceiverContactEmail: address.ReceiverContactEmail ?? null,
        ReceiverContactPhone: address.ReceiverContactPhone ?? null,
      }
    }
    return base
  })
}

export function addSelectedLinesToKit(lines: editingLineItemType[], kit: string): editingLineItemType[] {
  const selected = lines.filter(item => item.selected)
  const withoutSelected = lines.filter(item => !selected.includes(item))
  const lastInKitIndex =
    withoutSelected
      .map((item, i) => (item.Kit === kit ? i : -1))
      .filter(i => i >= 0)
      .pop() ?? -1
  const insertIndex = lastInKitIndex >= 0 ? lastInKitIndex + 1 : withoutSelected.length
  const withMoved = [
    ...withoutSelected.slice(0, insertIndex),
    ...selected.map(item => ({ ...item, Kit: kit, Number: null })),
    ...withoutSelected.slice(insertIndex),
  ]
  return renumberLines(withMoved)
}

export function zeroKitLinePrices(lines: editingLineItemType[], kit: string): editingLineItemType[] {
  return lines.map(item =>
    item.Kit === kit && item.Number !== -1 ? { ...item, UnitPrice: 0, UnitCost: 0, ExtPrice: 0, ExtCost: 0 } : item,
  )
}

export function createNewKitWithSelection(
  lines: editingLineItemType[],
  kitHeader: editingLineItemType,
): editingLineItemType[] {
  const selectedLines = lines.filter(item => item.selected)
  const selectedIndices = lines.map((item, i) => (selectedLines.includes(item) ? i : -1)).filter(i => i >= 0)
  const firstSelectedIndex = selectedIndices.length > 0 ? Math.min(...selectedIndices) : lines.length
  const lastSelectedIndex = selectedIndices.length > 0 ? Math.max(...selectedIndices) : -1
  const kitId = kitHeader.Kit
  return renumberLines([
    ...lines.slice(0, firstSelectedIndex),
    kitHeader,
    ...selectedLines.map(item => ({ ...item, Kit: kitId })),
    ...lines.slice(lastSelectedIndex + 1),
  ])
}

export function unkitLines(lines: editingLineItemType[], kit: string): editingLineItemType[] {
  return renumberLines(
    lines
      .filter(item => !(item.Kit === kit && item.Number === -1))
      .map(item => (item.Kit === kit ? { ...item, Kit: null } : item)),
  )
}

export function flattenLineItems(items: lineItemType[]): lineItemType[] {
  const result: lineItemType[] = []
  for (const line of items) {
    if (line.children?.length) {
      result.push(line)
      for (const child of line.children) {
        result.push(child)
      }
    } else {
      result.push(line)
    }
  }
  return result
}

export function unflattenLineItems(flat: lineItemType[]): lineItemType[] {
  const result: lineItemType[] = []
  let i = 0
  while (i < flat.length) {
    const item = { ...flat[i] }
    if (item.Number === -1 && item.Kit != null) {
      const children: lineItemType[] = []
      i++
      while (i < flat.length && flat[i].Kit === item.Kit && (flat[i].Number ?? -2) !== -1) {
        children.push({ ...flat[i] })
        i++
      }
      result.push({ ...item, children })
    } else {
      result.push(item)
      i++
    }
  }
  return result
}

export function stripEditingFields(items: editingLineItemType[]): lineItemType[] {
  return items.map(({ editing: _e, addressId: _a, selected: _s, ...rest }) => rest as lineItemType)
}

type PricingSource = {
  Quantity: number | null
  UnitCost: number | null
  ExtCost: number | null
  UnitPrice: number | null
  ExtPrice: number | null
}

export function updatePricing(
  item: PricingSource,
  field: 'Quantity' | 'UnitCost' | 'ExtCost' | 'UnitPrice' | 'ExtPrice',
  value: number | null,
): Partial<PricingSource> {
  const patch: Partial<PricingSource> = { [field]: value }
  const qty = field === 'Quantity' ? value : item.Quantity
  const unitCost = field === 'UnitCost' ? value : item.UnitCost
  const unitPrice = field === 'UnitPrice' ? value : item.UnitPrice
  const extCost = field === 'ExtCost' ? value : item.ExtCost
  const extPrice = field === 'ExtPrice' ? value : item.ExtPrice

  if (field === 'Quantity') {
    if (qty != null && unitCost != null) patch.ExtCost = unitCost * qty
    else if (qty != null && extCost != null && qty !== 0) patch.UnitCost = extCost / qty
    if (qty != null && unitPrice != null) patch.ExtPrice = unitPrice * qty
    else if (qty != null && extPrice != null && qty !== 0) patch.UnitPrice = extPrice / qty
  } else if (field === 'UnitCost') {
    if (qty != null && unitCost != null) patch.ExtCost = unitCost * qty
  } else if (field === 'ExtCost') {
    if (qty != null && qty !== 0 && extCost != null) patch.UnitCost = extCost / qty
  } else if (field === 'UnitPrice') {
    if (qty != null && unitPrice != null) patch.ExtPrice = unitPrice * qty
  } else {
    if (qty != null && qty !== 0 && extPrice != null) patch.UnitPrice = extPrice / qty
  }

  return patch
}
